import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/common/SEO/SEO';
import Image from '../../components/common/Image/ImageComponent';
import RequestFeatureSection from '../../components/home/RequestFeatureSection/RequestFeatureSection';
import RequestBanner from '../../components/common/RequestBanner/RequestBanner';
import PurpleWhiteButton from '../../components/common/PurpleWhiteButton/PurpleWhiteButton';
import Spinner from '../../components/common/Spinner/Spinner';
import apiService from '../../services/api';
import './HomePage.css';
import ProductCard from '../../components/common/ProductCard/ProductCard';
import { ArrowRight } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredLoot, setFeaturedLoot] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingFeaturedLoot, setLoadingFeaturedLoot] = useState(true);
  const handleRequestClick = () => {
    navigate('/request-item');
  };

  useEffect(() => {
    const fetchHomePageData = async () => {
      setLoadingProducts(true);
      setLoadingFeaturedLoot(true);
      try {
        const productsResponse = await apiService.products.getAll({
          limit: 5,
          sort: '-createdAt'
        });
        if (productsResponse.data.success) {
          setFeaturedProducts(productsResponse.data.data);
        }
        setLoadingProducts(false);

        const featuredLootResponse = await apiService.settings.getFeaturedLoot();
        if (featuredLootResponse.data.success) {
          setFeaturedLoot(featuredLootResponse.data.data);
        }
        setLoadingFeaturedLoot(false);

        setTestimonials([
          {
            id: 1,
            name: 'Lucy T.',
            quote: 'The most wonderful pin shop ever! Samra is the loveliest, most helpful person — she finds items even from vague descriptions. Absolutely magical service!'
          },
          {
            id: 2,
            name: 'Victoria A.',
            quote: 'Faith, trust and pixie dust! Samra has a real skill for finding exactly what you\'re looking for, paired with a superb level of communication throughout.'
          },
          {
            id: 3,
            name: 'Lynzi B.',
            quote: 'I have been using Samra\'s Pins for over a year and she goes above and beyond every single time. She has NEVER failed to deliver — truly incredible!'
          },
          {
            id: 4,
            name: 'Hannah A.',
            quote: 'Brilliant interactions and service — prompt shipping, great packaging, and always informative and positive. Could not recommend more highly!'
          },
          {
            id: 5,
            name: 'Victoria N.',
            quote: 'Practically perfect in every way! A Disney pin and merch legend who always provides helpful advice and makes the whole experience a joy.'
          },
          {
            id: 6,
            name: 'Venera',
            quote: 'Thank you for the pixie dust! Samra\'s care, kindness, and genuine passion for what she does made the whole process completely stress-free. Magical!'
          },
          {
            id: 7,
            name: 'Emily',
            quote: 'SUPER SELLER! Multiple gorgeous orders and so reasonably priced. Samra\'s pin sourcing is second to none — a pure delight to shop with every time.'
          }
        ]);

      } catch (err) {
        console.error('Error fetching homepage data:', err);
        setLoadingProducts(false);
        setLoadingFeaturedLoot(false);
      }
    };

    fetchHomePageData();
  }, []);

  return (
    <div className="home-page">
      <SEO
        title="Disney Pins & Merch Straight from the Parks"
        description="Shop authentic Disney pins, lanyards, Loungefly bags, ears, and park-exclusive merch — sourced directly from Disney parks and delivered to your door."
        canonical="/"
      />
      {/* Request Banner */}
      <RequestBanner onRequestClick={handleRequestClick} />

      {/* Your Favourites Section */}
      <section className="homepage-favourites-section homepage-section">
        <div className="homepage-section-content">
          <div className="homepage-section-header-left">
            <h2>Your Favourites!</h2>
            <p className="homepage-section-subtitle">The easiest way to bring the magic home!</p>
            <button className="homepage-btn-see-more">
              <PurpleWhiteButton to="/category/all" text="Browse all" />
            </button>
          </div>

          <div className="homepage-products-container">
            {loadingProducts ? (
              <Spinner text="Loading products..." />
            ) : (
              <div className="homepage-product-grid">
                {featuredProducts.slice(0, 5).map(product => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Magic Requests Section */}
      <section className="homepage-magic-requests-section homepage-section">
        <div className="homepage-section-header-center">
          <h2>Magic Requests</h2>
          <p className="homepage-section-subtitle">Send a request and the fairies will grant your wishes!</p>
        </div>

        <div className="homepage-steps-container">
          <div className="homepage-step-card">
            <div className="homepage-step-icon homepage-yellow-circle">
              <svg width="81" height="81" viewBox="0 0 24 24" fill="none" stroke="#1E1E1E" strokeWidth="1">
                <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                <path d="M2 2l7.586 7.586"></path>
                <circle cx="11" cy="11" r="2"></circle>
              </svg>
            </div>
            <h3>Fill Out The Form</h3>
            <span className='homepage-step-subtitle'>Send your wishes for the fairies to review!</span>
          </div>

          <div className="homepage-step-card">
            <div className="homepage-step-icon homepage-yellow-circle">
              <svg width="81" height="81" viewBox="0 0 24 24" fill="none" stroke="#1E1E1E" strokeWidth="1">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
            </div>
            <h3>Collection On Next Visit</h3>
            <span className='homepage-step-subtitle'>You will be notified if the fairies can grant your wish!</span>
          </div>

          <div className="homepage-step-card">
            <div className="homepage-step-icon homepage-yellow-circle">
              <svg width="81" height="81" viewBox="0 0 24 24" fill="none" stroke="#1E1E1E" strokeWidth="1">
                <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </div>
            <h3>Magic To Your Door</h3>
            <span className='homepage-step-subtitle'>They will gather your pixie dust and deliver to your door!</span>
          </div>
        </div>

        <div className="homepage-request-search">
          <PurpleWhiteButton to="/request-item" text="Send a Request" />
        </div>
      </section>

      {/* Raid the Fairies' Loot Section */}
      <section className="homepage-loot-section homepage-section">
        <div className="homepage-section-header-center">
          <h2>Raid the Fairies' Loot</h2>
          <p className="homepage-section-subtitle">Uninspired? Find what you're looking for!</p>
        </div>

        {loadingFeaturedLoot ? (
          <Spinner text="Loading featured items..." />
        ) : (
          <>
            <div className="homepage-loot-grid">
              {featuredLoot.length > 0 ? (
                featuredLoot.map((product, index) => (
                  <Link
                    to={`/product/${product._id}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className={`homepage-loot-card ${index === 1 ? 'homepage-loot-card-middle' : 'homepage-loot-card-outer'}`}
                    key={product._id}
                  >
                    <div className="homepage-loot-image">
                      <img
                        src={product.mainImage || '/assets/images/placeholder-product.jpg'}
                        alt={product.name}
                      />
                    </div>
                    <h3>{product.name}</h3>
                  </Link>
                ))
              ) : (
                <p className="homepage-no-featured-items">No featured items selected yet.</p>
              )}
            </div>
            <div className="homepage-loot-button-container">
              <PurpleWhiteButton to="/category/all" text="Browse all" />
            </div>
          </>
        )}
      </section>

      {/* Reviews Section */}
      <section className="homepage-reviews-section homepage-section">
        <div className="homepage-section-header-left-alt">
          <h2>What customers say about Samra's Pins</h2>
        </div>

        <div className="homepage-reviews-carousel-container">
          <div className="homepage-reviews-carousel">
            {testimonials.map((testimonial) => (
              <div
                className="homepage-review-card active"
                key={testimonial.id}
              >
                <p className="homepage-review-text">{testimonial.quote}</p>
                <div className="homepage-review-footer">
                  <span className="homepage-review-quote">"</span>
                  <span className="homepage-reviewer-name">{testimonial.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;